/*
INTELLECTUAL PROPERTY NOTICE
© Kaleida Ltd and/or Orbit Tickets Ltd. All rights reserved.
This file contains a combination of:
- Protected Intellectual Property owned by Kaleida Ltd; and
- Custom / Included Intellectual Property owned by the Customer,
and forms part of an application developed by Kaleida within the Platform.
Protected Intellectual Property may only be used as incorporated within applications developed by Kaleida within the Platform and must not be used, reproduced, adapted, or incorporated into any application developed by or on behalf of the Customer (being any application not developed by Kaleida), whether or not such application forms part of the Platform.
Custom / Included Intellectual Property may be used, reproduced, adapted, and incorporated into any application, system, or product developed by or on behalf of the Customer, whether or not such application forms part of the Platform, subject always to the terms of the agreement and provided that such use does not include or rely upon any Protected Intellectual Property of Kaleida Ltd.
For the avoidance of doubt, where separation between Protected Intellectual Property and Custom / Included Intellectual Property is unclear, this file shall be treated as containing Protected Intellectual Property.
*/

// IP-NOTICE: MIXED
import { MainMenuService } from './../services/main-menu.service';
import { DefaultAsideComponent } from './../components/aside-container/default-aside.component';
import { AsideService } from 'app/layout/components/aside-container/aside.service';
import { ResponsiveService } from './../services/responsive.service';
import { AfterViewInit, Component, HostListener, OnInit, ViewContainerRef, ViewChild, Inject, OnDestroy } from '@angular/core';
import { ComponentItem } from '../../shared/base/aside/component-item';
import { UsersService } from 'app/modules/configuration/users/user-management/services/users.service';
import { KeepAliveService } from 'app/shared/services/keep-alive.service';
import { MatSidenav } from '@angular/material/sidenav';
import { environment } from 'environments/environment';
import { Subscription } from 'rxjs';
import { BlockTemplateComponent } from '../components/block-ui/block-template.component';

@Component({
    templateUrl: './layout.component.html',
    styleUrls: ['./layout.component.scss'],
    standalone: false
})
export class LayoutComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatSidenav, { static: false }) public sidenav: MatSidenav;
  readonly shellGridColumns = 12;
  readonly expandedNavigationColumns = 2;
  showMenu = false;
  asideOpenSubscription: Subscription;
  asideCloseSubscription: Subscription;
  resizeSubscription: Subscription;

  // defined globally so will be created once and use context to control behaviour
  blockTemplate: any = BlockTemplateComponent;

  constructor(
    protected usersService: UsersService,
    public mainMenuService: MainMenuService,
    public asideService: AsideService,
    public responsiveService: ResponsiveService,
    private keepAliveService: KeepAliveService
  ) {
  }

  get selectedTheme(): string {
    switch (environment.name.toUpperCase()) {
      case 'TEST':
      case 'UAT':
      case 'AWS-UAT':
        return 'test-theme';
      default:
        return 'main-theme';
    }
  }

  get navigationOpen(): boolean {
    return this.showMenu && this.mainMenuService.menuVisible;
  }

  get isCompactViewport(): boolean {
    return this.responsiveService.innerWidth <= 1023;
  }

  get navigationMode(): 'over' | 'side' {
    return this.isCompactViewport ? 'over' : 'side';
  }

  ngOnInit() {
    this.asideOpenSubscription = this.asideService.asideOpen.subscribe(() => {
      this.sidenav.open();
    });
    this.asideCloseSubscription = this.asideService.asideClose.subscribe(() => {
      this.sidenav.close();
    });
    this.resizeSubscription = this.responsiveService.resize.subscribe(() => {
      if (this.responsiveService.innerWidth <= 1023) {
        this.mainMenuService.menuVisible = false;
      } else {
        this.mainMenuService.menuVisible = true;
      }
    });

    window.addEventListener('focus', this.focus.bind(this));
  }

  ngOnDestroy() {
    this.asideOpenSubscription.unsubscribe();
    this.asideCloseSubscription.unsubscribe();
    this.resizeSubscription.unsubscribe();
    window.removeEventListener('focus', this.focus.bind(this));
  }

  focus(){
    console.log('focus window');
    this.keepAliveService.checkAuthentication()
  }

  ngAfterViewInit() {
    this.asideService.setComponent(new ComponentItem(DefaultAsideComponent, {}));

    this.usersService.ensurePermissions().then(() => {
      this.showMenu = this.usersService.userPermissions.action.menu.available;
      this.mainMenuService.menuVisible = this.showMenu && !this.responsiveService.isMobile();
    });
  }

  toggleMainMenu() {
    this.mainMenuService.toggleMenu();
  }

  onNavigationOpenedChange(opened: boolean) {
    if (!opened && this.isCompactViewport) {
      this.mainMenuService.closeMenu();
    }
  }

  closeMainMenuOnCompactViewport() {
    if (this.responsiveService.isMobile()) {
      this.mainMenuService.closeMenu();
    }
  }

  @HostListener('window:resize', ['$event'])
  onWindowResize(event: UIEvent) {
    this.responsiveService.onResize(event);
  }
}
